<template>
    <div class="safe-froala-wrapper">
        <froala
            v-if="shouldRender"
            :value="computedFroalaValue"
            :tag="tag"
            :config="mergedConfig"
            @update:value="handleUpdate"
            @error="handleError"
        />
    </div>
</template>

<script>
import { stripInlineTextColors } from '@/utilities/stripInlineTextColors.js';

export default {
    name: 'SafeFroalaWrapper',

    props: {
        value: {
            type: String,
            default: '',
        },

        tag: {
            type: String,
            default: 'textarea',
        },

        config: {
            type: Object,
            default: () => ({}),
        },
    },

    emits: ['update:value', 'initialized', 'error'],

    data() {
        return {
            shouldRender: false,
            internalValue: '',
            isInitialized: false,
            pendingValue: null,
            userIsTyping: false,
            typingTimeout: null,
            froalaValue: '', // Separate value for Froala to prevent unwanted updates
        };
    },

    computed: {
        // Computed property to control what value Froala receives
        computedFroalaValue() {
            // If user is typing, don't change the Froala value
            if (this.userIsTyping) {
                return this.froalaValue;
            }
            // Otherwise, use the internal value
            return this.internalValue;
        },

        mergedConfig() {
            const vm = this;
            const blurHandler = this.config?.events?.blur;
            const parentPasteAfterCleanup = this.config?.events?.['paste.afterCleanup'];

            return {
                ...this.config,
                // Word paste defaults include `color`; drop it so Word/Docs pastes
                // don't persist hard-coded text colors that break light/dark themes.
                wordAllowedStyleProps: [
                    'font-family',
                    'font-size',
                    'background',
                    'width',
                    'text-align',
                    'vertical-align',
                    'background-color',
                    'padding',
                    'margin',
                    'height',
                    'margin-top',
                    'margin-left',
                    'margin-right',
                    'margin-bottom',
                    'text-decoration',
                    'font-weight',
                    'font-style',
                    'text-indent',
                    'border',
                    'border-.*',
                ],
                events: {
                    ...this.config.events,
                    // Must chain the parent's `initialized` (e.g. Editor.vue sets editorInstance + disable).
                    // Using `function` preserves Froala's `this` as the editor instance.
                    initialized(...args) {
                        vm.isInitialized = true;

                        if (vm.pendingValue !== null) {
                            vm.internalValue = vm.pendingValue;
                            vm.froalaValue = vm.pendingValue;
                            vm.pendingValue = null;
                        }

                        const parentInitialized = vm.config?.events?.initialized;
                        if (typeof parentInitialized === 'function') {
                            parentInitialized.apply(this, args);
                        }

                        vm.$emit('initialized', this);
                    },
                    contentChanged: () => {
                        this.markUserTyping();
                    },
                    keydown: () => {
                        this.markUserTyping();
                    },
                    input: () => {
                        this.markUserTyping();
                    },
                    'paste.before': () => {
                        this.markUserTyping();
                    },
                    'paste.afterCleanup': function (clipboardHtml) {
                        vm.markUserTyping();

                        let html = clipboardHtml;
                        if (typeof parentPasteAfterCleanup === 'function') {
                            const result = parentPasteAfterCleanup.call(this, clipboardHtml);
                            if (typeof result === 'string') {
                                html = result;
                            }
                        }

                        return stripInlineTextColors(html);
                    },
                    blur(...args) {
                        blurHandler?.apply(this, args);

                        if (this?.codeView?.isActive?.()) {
                            setTimeout(() => {
                                vm.handleCodeViewBlur(this);
                            }, 0);
                        }
                    },
                },
            };
        },
    },

    watch: {
        value: {
            immediate: true,
            handler(newValue) {
                // Only update if the new value is actually different from the current internal value
                // This prevents the editor from being cleared when the same value is set during autosave
                if (newValue !== this.internalValue) {
                    if (this.isInitialized) {
                        this.internalValue = newValue || '';
                        // Only update froalaValue if user is not typing
                        if (!this.userIsTyping) {
                            this.froalaValue = newValue || '';
                        }
                    } else {
                        this.pendingValue = newValue || '';
                    }
                }
            },
        },
    },

    mounted() {
        // Initialize internal value immediately to prevent race conditions
        this.internalValue = this.value || '';
        this.froalaValue = this.value || '';

        // Delay rendering to avoid race conditions
        this.$nextTick(() => {
            setTimeout(() => {
                this.shouldRender = true;
            }, 50);
        });
    },

    beforeUnmount() {
        // Clean up timeout
        if (this.typingTimeout) {
            clearTimeout(this.typingTimeout);
        }
    },

    methods: {
        markUserTyping() {
            // User is actively editing
            this.userIsTyping = true;

            // Clear any existing timeout
            if (this.typingTimeout) {
                clearTimeout(this.typingTimeout);
            }

            // Set a timeout to mark user as no longer typing
            this.typingTimeout = setTimeout(() => {
                this.userIsTyping = false;
            }, 2000); // 2 seconds after last change
        },

        handleUpdate(value) {
            // Only update and emit if the value has actually changed
            if (this.internalValue !== value) {
                this.internalValue = value;
                this.froalaValue = value; // Keep froalaValue in sync when user types
                this.$emit('update:value', value);
            }
        },

        handleCodeViewBlur(editor) {
            const value = editor?.codeView?.get?.();

            if (typeof value !== 'string') {
                return;
            }

            this.markUserTyping();
            this.handleUpdate(value);
        },

        handleError(error) {
            this.$emit('error', error);
        },
    },
};
</script>

<style scoped>
.safe-froala-wrapper {
    min-height: 60px;
}
:deep(.fr-box) {
    color: black
}
</style>
